import { Evaluation, EvaluationRequest, OPRFClient, OPRFServer, Oprf, deriveKeyPair, type FinalizeData } from "@cloudflare/voprf-ts";
import { CryptoNoble } from "@cloudflare/voprf-ts/crypto-noble";
import { base64 } from "@scure/base";
import { isValidPin } from "./keycopy.js";

/**
 * PIN hardening for the server key copy (PLAN.md 4.6), RFC 9497 OPRF mode.
 *
 *   browser: blindPin(pin)                -> request (blinded PIN, random every time)
 *   server:  evaluateBlindedPin(seed, ..) -> response; the server counts this as one attempt
 *   browser: finalizePin(state, response) -> F(k, PIN), 64 bytes, same PIN -> same value
 *
 * The server never sees the PIN or F(k, PIN); the browser never sees k.
 * Suite ristretto255-SHA512 with the @noble/curves provider: the fastest suite on Workers
 * (about 2 ms per evaluation, measured 2026-10-07).
 *
 * k is derived per identity from one server seed (Worker secret OPRF_SEED) and the pubkey of
 * the key copy. A member can therefore only ever evaluate the PIN function of their own copy:
 * a table of F(k, PIN) built with one's own attempts is useless against anyone else's copy.
 */
export const OPRF_SUITE = Oprf.Suite.RISTRETTO255_SHA512;
const PROVIDER = [CryptoNoble] as const;
const KEY_INFO_PREFIX = "vrelis oprf key v1|";

export class OprfError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "OprfError";
  }
}

/** Opaque client state between blindPin and finalizePin. Keep it in memory only. */
export type PinBlinding = { readonly finData: FinalizeData };

export async function blindPin(pin: string): Promise<{ request: string; state: PinBlinding }> {
  // A malformed PIN must not cost one of the member's server attempts.
  if (!isValidPin(pin)) throw new OprfError("PIN must be 6 to 32 digits");
  const client = new OPRFClient(OPRF_SUITE, ...PROVIDER);
  const [finData, req] = await client.blind([new TextEncoder().encode(pin)]);
  return { request: base64.encode(req.serialize()), state: { finData } };
}

export async function finalizePin(state: PinBlinding, response: string): Promise<Uint8Array> {
  let evaluation: Evaluation;
  try {
    evaluation = Evaluation.deserialize(OPRF_SUITE, base64.decode(response), ...PROVIDER);
  } catch {
    throw new OprfError("malformed OPRF response");
  }
  if (evaluation.evaluated.length !== 1) throw new OprfError("OPRF response must hold exactly one element");
  const [output] = await new OPRFClient(OPRF_SUITE, ...PROVIDER).finalize(state.finData, evaluation);
  return output;
}

/** Server seed from the OPRF_SEED Worker secret: 64 hex characters. */
export function parseOprfSeed(hex: string | undefined): Uint8Array {
  if (!hex || !/^[0-9a-fA-F]{64}$/.test(hex.trim())) throw new OprfError("OPRF_SEED must be 64 hex characters (32 bytes)");
  const clean = hex.trim();
  return Uint8Array.from({ length: 32 }, (_, i) => parseInt(clean.slice(i * 2, i * 2 + 2), 16));
}

async function serverFor(seed: Uint8Array, keyId: string): Promise<OPRFServer> {
  if (seed.length !== 32) throw new OprfError("OPRF seed must be 32 bytes");
  const { privateKey } = await deriveKeyPair(Oprf.Mode.OPRF, OPRF_SUITE, seed, new TextEncoder().encode(KEY_INFO_PREFIX + keyId), ...PROVIDER);
  return new OPRFServer(OPRF_SUITE, privateKey, ...PROVIDER);
}

/**
 * Server side: evaluates one blinded PIN under the key for `keyId` (the copy's pubkey).
 * Rejects batches, so one request is always exactly one PIN attempt.
 */
export async function evaluateBlindedPin(seed: Uint8Array, keyId: string, request: string): Promise<string> {
  let req: EvaluationRequest;
  try {
    req = EvaluationRequest.deserialize(OPRF_SUITE, base64.decode(request), ...PROVIDER);
  } catch {
    throw new OprfError("malformed OPRF request");
  }
  if (req.blinded.length !== 1) throw new OprfError("OPRF request must hold exactly one blinded PIN");
  if (req.blinded[0].isIdentity()) throw new OprfError("OPRF request holds the identity element");
  const evaluation = await (await serverFor(seed, keyId)).blindEvaluate(req);
  return base64.encode(evaluation.serialize());
}

/** Unblinded F(k, PIN). Only for tests and tools: the server never learns a PIN in normal use. */
export async function evaluatePinDirectly(seed: Uint8Array, keyId: string, pin: string): Promise<Uint8Array> {
  return (await serverFor(seed, keyId)).evaluate(new TextEncoder().encode(pin));
}
